"use client";

import React from "react";
import { XMarkIcon } from "@heroicons/react/16/solid";
import SelectPill from "./SelectPill";
import { PLATFORMS, FOLLOWER_RANGES } from "@/app/constants/creatorNetwork";
import type { SocialProfile } from "@/types/creatorNetwork";

type PlatformPickerProps = {
  profile: SocialProfile;
  index: number;
  isPrimary: boolean;
  onChange: (patch: Partial<SocialProfile>) => void;
  onRemove: () => void;
  errors?: Record<string, string | null | undefined>;
};

export default function PlatformPicker({
  profile,
  index,
  isPrimary,
  onChange,
  onRemove,
  errors = {},
}: PlatformPickerProps) {
  const platform = PLATFORMS.find((p) => p.value === profile.platform);

  const updateLink = (i: number, value: string) => {
    const links = [...profile.contentLinks];
    links[i] = value;
    onChange({ contentLinks: links });
  };

  const addLink = () => {
    if (profile.contentLinks.length >= 3) return;
    onChange({ contentLinks: [...profile.contentLinks, ""] });
  };

  const removeLink = (i: number) => {
    onChange({ contentLinks: profile.contentLinks.filter((_, idx) => idx !== i) });
  };

  return (
    <div className="pblock border-t border-white/15 mt-5 pt-5 sm:px-4">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <span className="block font-mono uppercase tracking-[0.15em] text-turquoise body-small-fontsize mb-1">
            {isPrimary ? "Primary platform" : `Platform ${index + 1}`}
          </span>
          <span className="text-white font-bold text-lg">{platform?.name}</span>
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="text-white/50 hover:text-red flex items-center gap-1 text-sm shrink-0"
        >
          <XMarkIcon className="w-4 h-4" /> Remove
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-2">
        <div>
          <input
            type="text"
            name={`handle-${index}`}
            value={profile.handle}
            onChange={(e) => onChange({ handle: e.target.value })}
            placeholder="@handle or profile URL"
            className="!text-white"
          />
          {errors.handle && (
            <div className="mt-1 pl-4 text-red text-sm">{errors.handle}</div>
          )}
        </div>

        <div>
          <SelectPill
            name={`followerRange-${index}`}
            value={profile.followerRange}
            onChange={(v) => onChange({ followerRange: v })}
            options={FOLLOWER_RANGES}
            placeholder="Follower range"
          />
          {errors.followerRange && (
            <div className="mt-1 pl-4 text-red text-sm">{errors.followerRange}</div>
          )}
        </div>
      </div>

      <div className="mt-5">
        <span className="block text-white mb-2">
          Content links
          <span className="text-white/50 text-sm ml-2">optional, up to 3</span>
        </span>

        {profile.contentLinks.length > 0 && (
          <div className="flex flex-col gap-2.5 mb-2.5">
            {profile.contentLinks.map((link, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="url"
                  value={link}
                  onChange={(e) => updateLink(i, e.target.value)}
                  placeholder="https://..."
                  className="!text-white"
                />
                <button
                  type="button"
                  aria-label="Remove link"
                  onClick={() => removeLink(i)}
                  className="text-white/50 hover:text-red shrink-0"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        )}
        {errors.contentLinks && (
          <div className="mt-1 text-red text-sm">{errors.contentLinks}</div>
        )}
        {profile.contentLinks.length < 3 && (
          <button
            type="button"
            onClick={addLink}
            className="text-sm font-semibold text-green underline"
          >
            + Add link
          </button>
        )}
      </div>
    </div>
  );
}
